/*
 *  Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import dayjs from "dayjs";
import { Text, theme } from "@nordcloud/gnui";
import { DateRange } from "~/components";
import { EstatePeriod } from "~/components/DateRangeSelector";
import { dateFormat } from "~/constants";
import { isNotNil } from "~/tools";

type Props = {
  period: EstatePeriod;
  range: DateRange | undefined;
};

export function PeriodSelection({ period, range }: Props) {
  return (
    <Text
      weight="medium"
      ml={theme.spacing.spacing03}
      my={theme.spacing.spacing04}
      data-testid="period-selection"
    >
      {getSelectedDates(period, range)}
    </Text>
  );
}

function getSelectedDates(period: EstatePeriod, range?: DateRange) {
  if (period === EstatePeriod.RANGE && isNotNil(range)) {
    return getRangeDates(range);
  }

  return getMonthDates(period);
}

function getMonthDates(period: EstatePeriod) {
  const today = dayjs();
  if (period === EstatePeriod.PREVIOUS_MONTH) {
    const previousMonth = today.add(-1, "month").endOf("month").toString();

    return `1 - ${formatDate(previousMonth)}`;
  }

  return `1 - ${formatDate(today.toString())}`;
}

function getRangeDates(range: DateRange) {
  const { from, to } = range;
  return `${formatDate(from)} - ${formatDate(to)}`;
}

function formatDate(date: string) {
  return `${dayjs(date).format(dateFormat.dayShort)} ${dayjs(date).format(
    dateFormat.monthName
  )} ${dayjs(date).format(dateFormat.year)}`;
}
